import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Asset } from 'app/models/document';
import { LpmConfig } from 'app/models/lpm.config';
import { Util } from 'app/models/util';
import { Permission } from 'app/modules/admin/apps/file-manager/permissions/permissions.types';
import { BehaviorSubject, Observable, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
    // Private
    private _permissions: BehaviorSubject<any> = new BehaviorSubject(null);
    rootFolderId:string = null;
    lpmConfig:LpmConfig = new LpmConfig();

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient, private util: Util) {
        this.rootFolderId = this.util.getRootFromSession();
        this.lpmConfig = this.util.getWebComponentInputParams().lpmConfig;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notes
     */
    get permissions(): Observable<any> {
        return this._permissions.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get list of permissions issued to list of users with access to the asset (i.e. Sharing & Permissions)
     * 
     */
    getPermissions(): Observable<any> {
        return this._httpClient.get<Permission[]>('api/apps/file-manager/permissions').pipe(
            tap((response: Permission[]) => {
                this._permissions.next(response);
            }),
        );
    }

    canDelete(item: Asset = new Asset()): boolean {
        const hasWriteAccess = item?.accessLevel == 'WRITER' || item.accessLevel == 'OWNER';
        const isNotLocked = !this.util.isLockedForCurrentUser(item);
        const isDeletable = item?.deletable;
        return isDeletable && isNotLocked && hasWriteAccess;
    }

    canView(item: Asset = new Asset()): boolean {
        const isFile = item?.type === 'FILE';
        const isVideo = item?.fileType === 'VIDEO';
        const isAudio = item?.fileType === 'AUDIO';
        return isFile && !isVideo && !isAudio;
    }

    canEdit(item: Asset = new Asset()): boolean {
        return false; // Disable Edit for now, as we are not sure about the use case and also it will require more work to integrate with editor. We can enable it in future when we have more clarity on the use case.
        return this.util.isSupportedFileTypeForEdit(item) && !this.util.getIsWebApp();
    }

    canMove(item: Asset = new Asset()): boolean {
        const hasWriteAccess = item?.accessLevel == 'WRITER' || item.accessLevel == 'OWNER';
        const isNotLocked = !this.util.isLockedForCurrentUser(item);
        const isDeletable = item?.deletable;
        const allowedMove = this.lpmConfig?.allowMove ?? true;
        return isDeletable && allowedMove && isNotLocked && hasWriteAccess;
    }

    canShare(item: Asset = new Asset()): boolean {
        // return item?.shareable ?? true;
        return true;
    }

    canRename(item: Asset = new Asset()): boolean {
        const hasWriteAccess = item?.accessLevel == 'WRITER' || item.accessLevel == 'OWNER';
        const isNotLocked = !this.util.isLockedForCurrentUser(item);
        const isDeletable = item?.deletable;
        return isDeletable && isNotLocked && hasWriteAccess;
    }

    // Feature-level visibility only (type + lpmConfig flag) - lock state is handled separately as a disable reason.
    isOpenOnDesktopVisible(item: Asset = new Asset()): boolean {
        return item?.type === 'FILE' && this.isOpenOnDesktopEnabled();
    }

    isOpenOnDesktopEnabled(): boolean {
        return this.lpmConfig?.allowOpenOnDesktop ?? true;
    }

}
